// frontend/src/pages/admin/AccountApprovals.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useAppContext } from '../../context/AppContext';
import dayjs from 'dayjs';
import { ACCOUNT_REQUEST_STATUSES } from '../../const/accountRequestStatuses';
import PropTypes from 'prop-types';
import { Table, Button, Space, Modal, Input, Form, Spin, Alert } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useNotificationApi } from '../../context/NotificationContext';

const { TextArea } = Input;

const AccountApprovals = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [currentRequestId, setCurrentRequestId] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [form] = Form.useForm();

  const { 
    user, 
    isAuthenticated, 
    loading: authLoading, 
    hasPermission,
    PERMISSION_LEVELS
  } = useAuth();
  const { appSettings } = useAppContext();
  const notificationApi = useNotificationApi();

  const dateFormat = appSettings?.dateFormat || 'DD/MM/YYYY';
  const timeFormat = appSettings?.timeFormat || 'hh:mm A';

  const canManageAccountRequests = hasPermission('accountRequests', 'edit', PERMISSION_LEVELS.ALL);

  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;

    const startTime = Date.now();

    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  const fetchPendingRequests = useCallback(async () => {
    if (authLoading || !isAuthenticated) {
      console.warn('AccountApprovals: Authentication not ready or not authenticated. Skipping data fetch.');
      setLoading(false);
      if (!isAuthenticated) {
        showError('Authentication Required', 'Please login to view account approval requests.', 'authRequiredError');
      }
      return;
    }

    if (!canManageAccountRequests) {
      console.warn('AccountApprovals: User does not have required permissions. Skipping data fetch.');
      setLoading(false);
      showError('Permission Denied', 'You do not have permission to view account approval requests.', 'permissionError');
      return;
    }

    setLoading(true);
    const loadingKey = 'fetchRequestsLoading';
    const hideLoading = showLoading(loadingKey, 'Loading pending account requests...');

    try {
      notificationApi.destroy('globalError');
      notificationApi.destroy('globalSuccess');

      const response = await api.get('/api/account-requests/pending', {
        params: { _: Date.now() }
      });

      let fetchedRequests = response.data;

      if (typeof fetchedRequests === 'object' && fetchedRequests !== null) {
        if (Array.isArray(fetchedRequests.data)) {
          fetchedRequests = fetchedRequests.data;
        } else if (Array.isArray(fetchedRequests.requests)) {
          fetchedRequests = fetchedRequests.requests;
        } else if (!Array.isArray(fetchedRequests)) {
          console.error('AccountApprovals: Response object does not contain an array. Assuming empty array.');
          fetchedRequests = [];
        }
      }
      if (!Array.isArray(fetchedRequests)) {
        throw new Error('Expected array of requests but received a non-array format after extraction.');
      }

      setRequests(fetchedRequests);
      hideLoading();
      showSuccess('Requests Loaded', 'Pending account requests fetched successfully!', 'requestsLoadedSuccess');

    } catch (err) {
      hideLoading();
      console.error('AccountApprovals: Failed to fetch requests:', {
        error: err,
        response: err.response,
        message: err.message
      });

      let errorMsg = 'Failed to load requests';
      if (err.message.includes('Expected array')) {
        errorMsg = err.message;
      } else if (err.response) {
        if (err.response.status === 401) {
          errorMsg = 'Session expired. Please login again.';
        } else if (err.response.status === 403) {
          errorMsg = 'You do not have permission to view requests.';
        } else if (err.response.status === 500) {
          errorMsg = 'Server error. Please try again later.';
        } else if (err.response.data?.message) {
          errorMsg = err.response.data.message;
        }
      } else if (err.message) {
        errorMsg = err.message;
      }

      showError('Failed to Fetch Requests', errorMsg, 'fetchRequestsError');
    } finally {
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, canManageAccountRequests, notificationApi, showError, showSuccess, showLoading]);

  useEffect(() => {
    fetchPendingRequests();
  }, [fetchPendingRequests]);

  const handleRejectClick = (requestId) => {
    setCurrentRequestId(requestId);
    setRejectionReason('');
    form.resetFields();
    setShowRejectModal(true);
    notificationApi.destroy('rejectionReasonError');
  };

  const closeRejectModal = () => {
    setShowRejectModal(false);
    setCurrentRequestId(null);
    setRejectionReason('');
    form.resetFields();
    notificationApi.destroy('rejectionReasonError');
  };

  const handleApproval = async (requestId, action, reason = null) => {
    try {
      setProcessingId(requestId);
      notificationApi.destroy('globalError');
      notificationApi.destroy('globalSuccess');
      notificationApi.destroy('rejectionReasonError');

      let payload = {};
      if (action === 'reject') {
        if (!reason || reason.trim() === '') {
          showError('Rejection Reason Required', 'Please provide a reason for rejecting this account request.', 'rejectionReasonError');
          setProcessingId(null);
          return;
        }
        payload = { rejectionReason: reason.trim() };
      }

      const loadingKey = `requestAction-${requestId}`;
      const hideLoading = showLoading(loadingKey, `${action === 'approve' ? 'Approving' : 'Rejecting'} request...`);

      const requestToProcess = requests.find(req => req.id === requestId); 
      const applicantFirstName = requestToProcess ? requestToProcess.firstName : '';
      const applicantLastName = requestToProcess ? requestToProcess.lastName : '';

      const response = await api.put(`/api/account-requests/${requestId}/${action}`, payload, {
        params: { _: Date.now() }
      });

      hideLoading();
      showSuccess(
        `${action === 'approve' ? 'Approval' : 'Rejection'} Successful`, 
        `Request for ${applicantFirstName.trim()} ${applicantLastName.trim()} was ${action === 'approve' ? 'approved' : 'rejected'}.`, 
        `actionSuccess-${requestId}`
      );
      setRequests(prev => prev.filter(req => req.id !== requestId));
      closeRejectModal();

    } catch (err) {
      setProcessingId(null);
      const apiError = err.response?.data;
      let errorMsg = `Failed to ${action} request`;
      if (apiError?.message) {
        errorMsg = apiError.message;
      } else if (err.response?.status === 500) {
        errorMsg = 'Server error. Please try again later.';
      } else if (err.message) {
        errorMsg = err.message;
      }
      showError(`Failed to ${action === 'approve' ? 'Approve' : 'Reject'} Request`, errorMsg, `actionError-${requestId}`);
    } finally {
      setProcessingId(null);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'firstName',
      key: 'name',
      render: (text, record) => {
        const firstName = record.firstName ? record.firstName.trim() : '';
        const lastName = record.lastName ? record.lastName.trim() : '';
        return `${firstName}${firstName && lastName ? ' ' : ''}${lastName}`;
      },
      sorter: (a, b) => {
        const nameA = `${a.firstName ? a.firstName.trim() : ''}${a.firstName && a.lastName ? ' ' : ''}${a.lastName ? a.lastName.trim() : ''}`;
        const nameB = `${b.firstName ? b.firstName.trim() : ''}${b.firstName && b.lastName ? ' ' : ''}${b.lastName ? b.lastName.trim() : ''}`;
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => (a.email || '').localeCompare(b.email || ''),
    },
    {
      title: 'Organization',
      dataIndex: 'organizationName',
      key: 'organization',
      sorter: (a, b) => (a.organizationName || '').localeCompare(b.organizationName || ''),
      render: (text) => text || 'N/A',
    },
    {
      title: 'Type',
      dataIndex: 'userType',
      key: 'type',
      render: (type) => type ? type.replace(/_/g, ' ').toUpperCase() : 'N/A',
      sorter: (a, b) => (a.userType || '').localeCompare(b.userType || ''),
    },
    {
      title: 'Request Date',
      dataIndex: 'createdAt',
      key: 'requestDate',
      render: (date) => date ? dayjs(date).format(`${dateFormat} ${timeFormat}`) : 'N/A',
      sorter: (a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={() => handleApproval(record.id, 'approve')}
            loading={processingId === record.id}
            disabled={processingId !== null && processingId !== record.id}
            aria-label={`Approve ${record.firstName} ${record.lastName}'s request`}
          >
            Approve
          </Button>
          <Button
            type="default"
            icon={<CloseCircleOutlined />}
            onClick={() => handleRejectClick(record.id)}
            loading={processingId === record.id}
            disabled={processingId !== null && processingId !== record.id}
            aria-label={`Reject ${record.firstName} ${record.lastName}'s request`}
          >
            Reject
          </Button>
        </Space>
      ),
    },
  ];

  if (authLoading) {
    return (
      <div className="account-approvals-page">
        <Spin size="large" tip="Authenticating..." />
      </div>
    );
  }

  if (!isAuthenticated || !canManageAccountRequests) {
    return (
      <div className="account-approvals-page">
        <Alert
          message="Access Denied"
          description="You do not have the necessary permissions to view this page. Please contact your administrator if you believe this is an error."
          type="error"
          showIcon
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="account-approvals-page">
        <Spin size="large" tip="Loading pending account requests..." />
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="account-approvals-page">
        <div className="account-approvals-header">
          <h2>No Pending Account Requests</h2>
        </div>
        <Alert
          message="No Pending Requests"
          description="There are currently no new account requests awaiting your approval."
          type="info"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="account-approvals-page">
      <div className="account-approvals-header">
        <h2>Pending Account Requests</h2>
      </div>

      <Table
        columns={columns}
        dataSource={requests}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: true }}
      />

      <Modal
        title="Reject Account Request"
        visible={showRejectModal}
        onCancel={closeRejectModal}
        footer={[
          <Button key="back" onClick={closeRejectModal} disabled={processingId !== null}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            danger
            loading={processingId === currentRequestId}
            onClick={() => form.submit()}
            disabled={processingId !== null}
          >
            {processingId === currentRequestId ? 'Submitting...' : 'Confirm Reject'}
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => handleApproval(currentRequestId, 'reject', values.rejectionReason)}
        >
          <Form.Item
            name="rejectionReason"
            label="Please provide a reason for rejecting this account request:"
            rules={[{ required: true, message: 'Rejection reason is required!' }]}
          >
            <TextArea
              rows={4}
              placeholder="Reason for rejection..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

AccountApprovals.propTypes = {};

export default AccountApprovals;